import { useMemo, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { useCookieConsent } from 'contexts/CookieConsentContext';

import { Modal } from '@polyai/ui/components/molecules/Modal';
import Tabs from '@polyai/ui/components/molecules/Tabs';
import TabPanel from '@polyai/ui/components/molecules/Tabs/TabPanel';

import AccountModalContent from './AccountModalContent';
import CookiesTabContent from './CookiesTabContent';

type AccountSettingsTab = 'profile' | 'cookies';

const PROFILE_TAB = { value: 'profile' as const, label: 'Profile' };
const COOKIES_TAB = { value: 'cookies' as const, label: 'Cookies' };

interface AccountModalProps {
  trigger?: React.ReactElement<Record<string, unknown>>;
  onOpen?: () => void;
  onClose?: () => void;
}

const AccountModal: React.FC<AccountModalProps> = ({
  trigger,
  onOpen,
  onClose,
}) => {
  const { user } = useAuth0();
  const { isCookiebotEnabled } = useCookieConsent();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<AccountSettingsTab>('profile');

  const tabs = useMemo(
    () => (isCookiebotEnabled ? [PROFILE_TAB, COOKIES_TAB] : [PROFILE_TAB]),
    [isCookiebotEnabled],
  );

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      onOpen?.();
    } else {
      onClose?.();
      // Reset to profile tab when closing
      setActiveTab('profile');
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={handleOpenChange}>
      {trigger && <Modal.Trigger render={trigger} />}
      <Modal.Content size="md">
        <Modal.Title>Account settings</Modal.Title>

        <div className="mt-sm2">
          <Tabs
            color="textPrimary"
            tabs={tabs}
            value={activeTab}
            withBorderBottom
            onChange={setActiveTab}
          />
        </div>

        <div className="mt-sm4 min-h-[280px]">
          <TabPanel currentValue={activeTab} value="profile">
            <AccountModalContent
              email={user?.email}
              firstName={user?.given_name}
              lastName={user?.family_name}
            />
          </TabPanel>

          {isCookiebotEnabled && (
            <TabPanel currentValue={activeTab} value="cookies">
              <CookiesTabContent />
            </TabPanel>
          )}
        </div>

        <Modal.Footer
          primary={{
            label: 'Close',
            onClick: () => handleOpenChange(false),
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};

export default AccountModal;
